import './sass/product-gallery.scss';
import {
    buildLinkedColorProductUrl,
    findBundleVariation,
    formatGalleryCounter,
    getNextGalleryIndex,
    isBundleOptionAvailable,
} from './js/product-page-utils.mjs';

document.documentElement.classList.add('has-product-page-js');

const mobileGalleryQuery = window.matchMedia('(max-width: 767px)');
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const drawerFocusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const playVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) {
        return;
    }

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.play()?.catch(() => {});
};

const pauseVideosExcept = (root, activeVideo = null) => {
    root.querySelectorAll('video').forEach((video) => {
        if (video === activeVideo) {
            playVideo(video);
        } else {
            video.pause();
        }
    });
};

const initVisibleVideos = (root) => {
    const videos = [...root.querySelectorAll('.SeluviaProductGallery__video')];

    if (!videos.length) {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        videos.forEach(playVideo);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (mobileGalleryQuery.matches) {
                return;
            }

            if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
                playVideo(entry.target);
            } else {
                entry.target.pause();
            }
        });
    }, { threshold: [0, 0.35] });

    videos.forEach((video) => observer.observe(video));
};

const initGalleryDialog = (gallery) => {
    const dialog = gallery.querySelector('[data-gallery-dialog]');
    const dialogMedia = dialog?.querySelector('[data-gallery-dialog-media]');
    const dialogStage = dialog?.querySelector('[data-gallery-dialog-stage]');
    const dialogViewport = dialog?.querySelector('[data-gallery-dialog-viewport]');
    const dialogCounter = dialog?.querySelector('[data-gallery-dialog-counter]');
    const dialogProgress = dialog?.querySelector('[data-gallery-progress]');
    const thumbnails = dialog?.querySelector('[data-gallery-thumbnails]');
    const closeButton = dialog?.querySelector('[data-gallery-close]');
    const previousButton = dialog?.querySelector('[data-gallery-previous]');
    const nextButton = dialog?.querySelector('[data-gallery-next]');
    const items = [...gallery.querySelectorAll('[data-gallery-item]')];

    if (!dialog || !dialogMedia || !dialogStage || !items.length) {
        return null;
    }

    // WooCommerce applies high-specificity sizing rules to every image nested
    // inside div.images. A modal belongs in the document top layer, so portal it
    // to body and keep the original media proportions independent from the grid.
    if (dialog.parentElement !== document.body) {
        document.body.append(dialog);
    }

    let index = 0;
    let returnFocus = null;
    let closeTimer = 0;
    let renderId = 0;
    let pointerStart = null;

    const getItemData = (item) => {
        const sourceImage = item?.querySelector('img');
        const sourceVideo = item?.querySelector('video');
        const source = sourceVideo?.querySelector('source');
        const type = item?.dataset.mediaType || 'image';

        return {
            type,
            url: item?.dataset.fullUrl
                || sourceImage?.dataset.large_image
                || sourceImage?.currentSrc
                || sourceImage?.src
                || source?.src
                || sourceVideo?.currentSrc
                || '',
            thumbnail: type === 'video'
                ? sourceVideo?.poster || sourceImage?.currentSrc || sourceImage?.src || ''
                : sourceImage?.currentSrc || sourceImage?.src || '',
            alt: item?.dataset.mediaAlt || sourceImage?.alt || '',
            mime: source?.type || '',
            poster: sourceVideo?.poster || '',
        };
    };

    const media = items.map(getItemData);

    const setLoading = (asset, currentRenderId) => {
        const finish = () => {
            if (currentRenderId === renderId) {
                dialogStage.classList.remove('is-loading');
            }
        };

        dialogStage.classList.add('is-loading');

        if (asset instanceof HTMLImageElement) {
            if (asset.complete) {
                finish();
            } else {
                asset.addEventListener('load', finish, { once: true });
                asset.addEventListener('error', finish, { once: true });
            }
        } else if (asset instanceof HTMLVideoElement) {
            if (asset.readyState >= 2) {
                finish();
            } else {
                asset.addEventListener('loadeddata', finish, { once: true });
                asset.addEventListener('error', finish, { once: true });
            }
        } else {
            finish();
        }
    };

    const createSlide = (item, itemIndex) => {
        const slide = document.createElement('figure');
        let asset;

        slide.className = 'SeluviaProductGalleryDialog__slide';
        slide.dataset.galleryDialogSlide = String(itemIndex);

        if (item.type === 'video') {
            const video = document.createElement('video');
            video.controls = true;
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.preload = 'metadata';
            video.poster = item.poster;
            video.setAttribute('aria-label', item.alt || 'Film produktu');

            if (item.mime) {
                const source = document.createElement('source');
                source.src = item.url;
                source.type = item.mime;
                video.append(source);
            } else {
                video.src = item.url;
            }

            asset = video;
        } else {
            const image = document.createElement('img');
            image.src = item.url;
            image.alt = item.alt;
            image.decoding = 'async';
            image.draggable = false;
            asset = image;
        }

        slide.append(asset);
        return { slide, asset };
    };

    const preloadNeighbours = () => {
        if (media.length < 2) {
            return;
        }

        [-1, 1].forEach((delta) => {
            const neighbour = media[getNextGalleryIndex(index, delta, media.length)];

            if (neighbour?.type === 'image' && neighbour.url) {
                const preload = new Image();
                preload.decoding = 'async';
                preload.src = neighbour.url;
            }
        });
    };

    const syncControls = () => {
        if (dialogCounter) {
            dialogCounter.textContent = formatGalleryCounter(index, media.length);
        }

        if (dialogProgress) {
            dialogProgress.style.width = `${((index + 1) / media.length) * 100}%`;
        }

        previousButton?.toggleAttribute('hidden', media.length < 2);
        nextButton?.toggleAttribute('hidden', media.length < 2);
        dialog.classList.toggle('is-single', media.length < 2);

        thumbnails?.querySelectorAll('[data-gallery-thumbnail]').forEach((button, buttonIndex) => {
            const active = buttonIndex === index;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-current', active ? 'true' : 'false');
            button.tabIndex = active ? 0 : -1;

            if (active && dialog.open) {
                button.scrollIntoView({
                    behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        });
    };

    const render = (nextIndex = index, direction = 0, shouldAnimate = true) => {
        const existingSlides = [...dialogMedia.querySelectorAll('[data-gallery-dialog-slide]')];
        const previousSlide = existingSlides.at(-1) || null;
        const nextItemIndex = Math.min(media.length - 1, Math.max(0, nextIndex));
        const { slide, asset } = createSlide(media[nextItemIndex], nextItemIndex);
        const currentRenderId = ++renderId;

        index = nextItemIndex;
        existingSlides.slice(0, -1).forEach((existingSlide) => existingSlide.remove());
        dialogMedia.querySelectorAll('video').forEach((video) => video.pause());
        dialogMedia.append(slide);
        setLoading(asset, currentRenderId);
        syncControls();
        preloadNeighbours();

        if (!previousSlide) {
            slide.classList.add('is-active');
            return;
        }

        if (!shouldAnimate || reducedMotionQuery.matches || !slide.animate) {
            previousSlide.remove();
            slide.classList.add('is-active');
            return;
        }

        const incomingOffset = direction < 0 ? -72 : 72;
        const outgoingOffset = incomingOffset * -0.58;
        const timing = {
            duration: 520,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'both',
        };

        slide.classList.add('is-active');
        slide.animate([
            { opacity: 0, transform: `translate3d(${incomingOffset}px, 0, 0) scale(0.985)` },
            { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
        ], timing);
        const outgoingAnimation = previousSlide.animate([
            { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
            { opacity: 0, transform: `translate3d(${outgoingOffset}px, 0, 0) scale(0.99)` },
        ], timing);

        outgoingAnimation.finished
            .catch(() => {})
            .finally(() => previousSlide.remove());
    };

    const buildThumbnails = () => {
        if (!thumbnails) {
            return;
        }

        const fragment = document.createDocumentFragment();

        media.forEach((item, itemIndex) => {
            const button = document.createElement('button');
            button.className = 'SeluviaProductGalleryDialog__thumbnail';
            button.type = 'button';
            button.dataset.galleryThumbnail = String(itemIndex);
            button.setAttribute('aria-label', `${item.type === 'video' ? 'Film' : 'Zdjęcie'} ${itemIndex + 1} z ${media.length}`);

            if (item.thumbnail) {
                const image = document.createElement('img');
                image.src = item.thumbnail;
                image.alt = '';
                image.loading = 'lazy';
                image.decoding = 'async';
                button.append(image);
            }

            if (item.type === 'video') {
                const badge = document.createElement('span');
                badge.className = 'SeluviaProductGalleryDialog__videoBadge';
                badge.setAttribute('aria-hidden', 'true');
                button.append(badge);
            }

            button.addEventListener('click', () => {
                if (itemIndex === index) {
                    return;
                }

                render(itemIndex, itemIndex > index ? 1 : -1);
            });
            fragment.append(button);
        });

        thumbnails.replaceChildren(fragment);
    };

    const open = (nextIndex = 0, trigger = null) => {
        window.clearTimeout(closeTimer);
        returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        dialog.classList.remove('is-closing', 'is-visible');
        render(Math.min(items.length - 1, Math.max(0, Number.parseInt(nextIndex, 10) || 0)), 0, false);

        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('has-product-gallery-open');
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => dialog.classList.add('is-visible'));
        });
        closeButton?.focus({ preventScroll: true });
    };

    const finishClose = () => {
        dialogMedia.querySelector('video')?.pause();

        if (typeof dialog.close === 'function' && dialog.open) {
            dialog.close();
        } else {
            dialog.removeAttribute('open');
        }

        dialog.classList.remove('is-closing', 'is-visible');
        document.documentElement.classList.remove('has-product-gallery-open');
        returnFocus?.focus?.({ preventScroll: true });
        returnFocus = null;
    };

    const close = () => {
        if (!dialog.open || dialog.classList.contains('is-closing')) {
            return;
        }

        dialogMedia.querySelector('video')?.pause();
        dialog.classList.remove('is-visible');
        dialog.classList.add('is-closing');

        if (reducedMotionQuery.matches) {
            finishClose();
            return;
        }

        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(finishClose, 280);
    };

    const move = (delta) => {
        if (media.length < 2) {
            return;
        }

        render(getNextGalleryIndex(index, delta, media.length), Math.sign(delta) || 1);
    };

    buildThumbnails();

    items.forEach((item, itemIndex) => {
        const link = item.querySelector('a');

        link?.addEventListener('click', (event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            event.preventDefault();
            open(itemIndex, link);
        });

        if (item.dataset.mediaType === 'video') {
            item.addEventListener('dblclick', () => open(itemIndex, item));
        }
    });

    gallery.querySelectorAll('[data-gallery-open]').forEach((button) => {
        button.addEventListener('click', () => open(button.dataset.galleryOpen, button));
    });
    closeButton?.addEventListener('click', close);
    previousButton?.addEventListener('click', () => move(-1));
    nextButton?.addEventListener('click', () => move(1));
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target === dialogStage || event.target === dialogMedia) {
            close();
        }
    });
    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
    });
    dialog.addEventListener('close', () => {
        dialogMedia.querySelector('video')?.pause();
        dialog.classList.remove('is-closing', 'is-visible');
        document.documentElement.classList.remove('has-product-gallery-open');
    });
    dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            move(-1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            move(1);
        } else if (event.key === 'Home') {
            event.preventDefault();
            render(0, -1);
        } else if (event.key === 'End') {
            event.preventDefault();
            render(media.length - 1, 1);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
        }
    });

    dialogViewport?.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.target instanceof Element && event.target.closest('button, video'))) {
            return;
        }

        pointerStart = { x: event.clientX, y: event.clientY };
    });
    dialogViewport?.addEventListener('pointerup', (event) => {
        if (!pointerStart || !event.isPrimary) {
            pointerStart = null;
            return;
        }

        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        pointerStart = null;

        if (Math.abs(deltaX) >= 52 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
            move(deltaX < 0 ? 1 : -1);
        }
    });
    dialogViewport?.addEventListener('pointercancel', () => {
        pointerStart = null;
    });

    return { open, close };
};

const initSizeGuideDialog = (dialog) => {
    if (!(dialog instanceof HTMLElement) || dialog.dataset.sizeGuideReady === 'true') {
        return;
    }

    const triggers = [...document.querySelectorAll('[data-size-guide-open]')].filter(
        (trigger) => trigger.getAttribute('aria-controls') === dialog.id,
    );
    const closeButtons = [...dialog.querySelectorAll('[data-size-guide-close]')];
    let returnFocus = null;
    let closeTimer = 0;

    const finishClose = () => {
        if (typeof dialog.close === 'function' && dialog.open) {
            dialog.close();
        } else {
            dialog.removeAttribute('open');
        }

        dialog.classList.remove('is-visible', 'is-closing');
        document.documentElement.classList.remove('has-size-guide-open');

        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
            returnFocus.focus({ preventScroll: true });
        }

        returnFocus = null;
    };

    const close = () => {
        if (!dialog.open || dialog.classList.contains('is-closing')) {
            return;
        }

        window.clearTimeout(closeTimer);
        dialog.classList.remove('is-visible');
        dialog.classList.add('is-closing');

        if (reducedMotionQuery.matches) {
            finishClose();
            return;
        }

        closeTimer = window.setTimeout(finishClose, 280);
    };

    const open = (trigger) => {
        window.clearTimeout(closeTimer);
        returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        dialog.classList.remove('is-visible', 'is-closing');

        if (!dialog.open && typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else if (!dialog.open) {
            dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('has-size-guide-open');
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => dialog.classList.add('is-visible'));
        });
        window.setTimeout(() => {
            dialog.querySelector('[data-size-guide-close]')?.focus({ preventScroll: true });
        }, reducedMotionQuery.matches ? 0 : 120);
    };

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            event.preventDefault();
            open(trigger);
        });
    });

    closeButtons.forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
            close();
        }
    });
    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
    });
    dialog.addEventListener('close', () => {
        dialog.classList.remove('is-visible', 'is-closing');
        document.documentElement.classList.remove('has-size-guide-open');
    });
    dialog.dataset.sizeGuideReady = 'true';
};

const initSizeGuides = () => {
    document.querySelectorAll('[data-size-guide-dialog]').forEach(initSizeGuideDialog);
};

const initMobileGallery = (gallery) => {
    const slider = gallery.querySelector('[data-gallery-slider]');
    const total = gallery.querySelectorAll('[data-gallery-item]').length;
    let swiper = null;

    if (!slider || !window.Swiper) {
        return;
    }

    const update = () => {
        if (mobileGalleryQuery.matches && !swiper) {
            swiper = new window.Swiper(slider, {
                slidesPerView: 1,
                spaceBetween: 0,
                speed: reducedMotionQuery.matches ? 0 : 460,
                grabCursor: true,
                keyboard: { enabled: true },
                pagination: {
                    el: gallery.querySelector('[data-gallery-pagination]'),
                    clickable: true,
                },
                on: {
                    slideChange(instance) {
                        const activeSlide = instance.slides[instance.activeIndex];
                        pauseVideosExcept(gallery, activeSlide?.querySelector('video'));
                    },
                },
            });
        } else if (!mobileGalleryQuery.matches && swiper) {
            swiper.destroy(true, true);
            swiper = null;
            pauseVideosExcept(gallery);
        }
    };

    update();
    mobileGalleryQuery.addEventListener?.('change', update);

    if (total <= 1) {
        gallery.querySelector('[data-gallery-pagination]')?.setAttribute('hidden', '');
    }
};

const initVariationOptions = (root) => {
    const form = root.querySelector('form.variations_form');

    if (!form) {
        return;
    }

    const groups = [...form.querySelectorAll('[data-product-options]')];
    const price = root.querySelector('[data-product-price]');
    const savings = root.querySelector('[data-product-savings]');
    const omnibus = root.querySelector('[data-product-omnibus]');
    const omnibusLabel = omnibus?.querySelector('[data-product-omnibus-label]');
    const omnibusPrice = omnibus?.querySelector('[data-product-omnibus-price]');
    const initialPrice = price?.innerHTML || '';
    const initialPricing = {
        savingsLabel: savings?.textContent?.trim() || '',
        savingsHidden: savings?.hidden ?? true,
        omnibusLabel: omnibusLabel?.textContent?.trim() || '',
        omnibusPrice: omnibusPrice?.innerHTML || '',
        omnibusHidden: omnibus?.hidden ?? true,
    };

    const ensureManagedColor = (group) => {
        if (group.dataset.familyManagedColor !== 'true') {
            return false;
        }

        const select = group.querySelector('select');
        const onlyOption = select
            ? [...select.options].find((option) => option.value !== '' && !option.disabled)
            : null;

        if (!select || select.value !== '' || !onlyOption) {
            return false;
        }

        select.value = onlyOption.value;
        return true;
    };

    const renderPricing = (pricing = null) => {
        const state = pricing
            ? {
                savingsLabel: String(pricing.savings_label || ''),
                savingsHidden: !pricing.promotion || !pricing.savings_label,
                omnibusLabel: String(pricing.omnibus_label || ''),
                omnibusPrice: String(pricing.omnibus_price_html || ''),
                omnibusHidden: !pricing.omnibus_label || !pricing.omnibus_price_html,
            }
            : initialPricing;

        if (savings) {
            savings.textContent = state.savingsLabel;
            savings.hidden = state.savingsHidden;
        }

        if (omnibus) {
            omnibus.hidden = state.omnibusHidden;
        }

        if (omnibusLabel) {
            omnibusLabel.textContent = state.omnibusLabel;
        }

        if (omnibusPrice) {
            omnibusPrice.innerHTML = state.omnibusPrice;
        }
    };

    const syncGroup = (group) => {
        const select = group.querySelector('select');
        const label = group.querySelector('[data-selected-option-label]');

        if (!select) {
            return;
        }

        group.querySelectorAll('[data-option-value]').forEach((button) => {
            const option = [...select.options].find((candidate) => candidate.value === button.dataset.optionValue);
            const selected = select.value !== '' && select.value === button.dataset.optionValue;
            const unavailable = !option || option.disabled;
            button.classList.toggle('is-selected', selected);
            button.classList.toggle('is-unavailable', unavailable);
            button.setAttribute('aria-pressed', String(selected));
            button.disabled = unavailable;
        });

        if (label) {
            const selectedButton = group.querySelector('.is-selected[data-option-label]');
            label.textContent = selectedButton?.dataset.optionLabel || '';
        }
    };

    const syncAll = () => groups.forEach((group) => {
        const select = group.querySelector('select');
        const restored = ensureManagedColor(group);
        syncGroup(group);

        if (restored) {
            select?.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });

    groups.forEach((group) => {
        const select = group.querySelector('select');
        group.addEventListener('click', (event) => {
            const button = event.target.closest('[data-option-value]');

            if (!button || button.disabled || !select) {
                return;
            }

            select.value = button.dataset.optionValue || '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
            syncGroup(group);
        });
        select?.addEventListener('change', () => {
            const restored = ensureManagedColor(group);
            syncGroup(group);

            if (restored) {
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    });

    form.querySelector('.reset_variations')?.addEventListener('click', () => {
        window.setTimeout(syncAll, 0);
    });

    if (window.jQuery) {
        const $form = window.jQuery(form);
        $form.on('woocommerce_update_variation_values.seluvia found_variation.seluvia reset_data.seluvia', syncAll);
        $form.on('found_variation.seluviaPrice', (_event, variation) => {
            if (price && variation?.price_html) {
                price.innerHTML = variation.price_html;
            }

            renderPricing(variation?.seluvia_pricing || null);
        });
        $form.on('reset_data.seluviaPrice hide_variation.seluviaPrice', () => {
            if (price) {
                price.innerHTML = initialPrice;
            }

            renderPricing();
        });
    }

    window.setTimeout(syncAll, 0);
};

const initColorFamily = (root) => {
    const family = root.querySelector('[data-product-color-family]');

    if (!family || family.dataset.colorFamilyReady === 'true') {
        return;
    }

    const links = [...family.querySelectorAll('[data-color-family-link]')];
    const variationForm = root.querySelector('form.variations_form');

    const selectedAttributes = () => {
        if (!variationForm) {
            return {};
        }

        return [...variationForm.querySelectorAll('select[name^="attribute_"]')].reduce((values, select) => {
            values[select.name] = select.value;
            return values;
        }, {});
    };

    const updateLinks = () => {
        const selections = selectedAttributes();

        links.forEach((link) => {
            const baseUrl = link.dataset.baseUrl || link.href;
            link.href = buildLinkedColorProductUrl(baseUrl, selections);
        });
    };

    links.forEach((link) => {
        link.addEventListener('click', (event) => {
            root.dispatchEvent(new CustomEvent('seluvia:color-product-select', {
                bubbles: true,
                detail: {
                    familyId: Number.parseInt(family.dataset.familyId || '0', 10) || 0,
                    sourceProductId: Number.parseInt(root.dataset.productId || '0', 10) || 0,
                    targetProductId: Number.parseInt(link.dataset.targetProductId || '0', 10) || 0,
                    color: link.dataset.colorLabel || '',
                },
            }));

            if (link.getAttribute('aria-current') === 'page') {
                event.preventDefault();
            }
        });
    });

    variationForm?.addEventListener('change', updateLinks);

    if (window.jQuery && variationForm) {
        window.jQuery(variationForm).on(
            'found_variation.seluviaColorFamily reset_data.seluviaColorFamily',
            updateLinks,
        );
    }

    updateLinks();
    family.dataset.colorFamilyReady = 'true';
};

const initDisclosures = (root) => {
    root.querySelectorAll('[data-product-disclosure]').forEach((details) => {
        const summary = details.querySelector('summary');
        const content = details.querySelector('.SeluviaProductDisclosure__content');

        if (!summary || !content || reducedMotionQuery.matches || !content.animate) {
            return;
        }

        summary.addEventListener('click', (event) => {
            if (details.dataset.animating === 'true') {
                event.preventDefault();
                return;
            }

            event.preventDefault();
            details.dataset.animating = 'true';
            const opening = !details.open;

            if (opening) {
                details.open = true;
            }

            const height = content.scrollHeight;
            const animation = content.animate(
                opening
                    ? [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }]
                    : [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
                { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
            );

            animation.addEventListener('finish', () => {
                if (!opening) {
                    details.open = false;
                }

                delete details.dataset.animating;
            }, { once: true });
            animation.addEventListener('cancel', () => {
                delete details.dataset.animating;
            }, { once: true });
        });
    });
};

const getCartApiConfig = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const endpoint = drawer?.dataset.cartEndpoint || '';

    if (!drawer || !endpoint) {
        return null;
    }

    return {
        drawer,
        endpoint: endpoint.replace(/\/$/, ''),
    };
};

const requestCartMutation = async (config, path, body, retryNonce = true) => {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Seluvia-Cart-Nonce': config.drawer.dataset.cartNonce || '',
    };

    if (config.drawer.dataset.restNonce) {
        headers['X-WP-Nonce'] = config.drawer.dataset.restNonce;
    }

    const response = await fetch(`${config.endpoint}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers,
        body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));

    if (response.status === 403 && retryNonce) {
        const refreshResponse = await fetch(config.endpoint, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
                Accept: 'application/json',
                ...(config.drawer.dataset.restNonce
                    ? { 'X-WP-Nonce': config.drawer.dataset.restNonce }
                    : {}),
            },
        });
        const refreshPayload = await refreshResponse.json().catch(() => ({}));

        if (refreshResponse.ok && refreshPayload?.nonce) {
            config.drawer.dataset.cartNonce = String(refreshPayload.nonce);
            config.drawer.dataset.restNonce = String(
                refreshPayload.rest_nonce || config.drawer.dataset.restNonce || '',
            );
            return requestCartMutation(config, path, body, false);
        }
    }

    if (!response.ok) {
        const error = new Error(payload?.message || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }

    if (payload?.nonce) {
        config.drawer.dataset.cartNonce = String(payload.nonce);
    }

    if (payload?.rest_nonce) {
        config.drawer.dataset.restNonce = String(payload.rest_nonce);
    }

    return payload;
};

const requestCartAddition = (config, body) => requestCartMutation(config, '/items', body);
const requestBundleAddition = (config, body) => requestCartMutation(config, '/bundles', body);

const initBundleDrawer = (root) => {
    const triggers = [...root.querySelectorAll('[data-bundle-open], [data-bundle-section-open]')];
    const primaryTrigger = triggers[0] || null;
    const drawerId = primaryTrigger?.getAttribute('aria-controls') || '';
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    const surface = drawer?.querySelector('[data-bundle-surface]');

    if (!primaryTrigger || !drawer || !surface || drawer.dataset.bundleReady === 'true') {
        return null;
    }

    const closeButtons = [...drawer.querySelectorAll('[data-bundle-close]')];
    const picker = drawer.querySelector('[data-bundle-picker]');
    const tabs = [...drawer.querySelectorAll('[data-bundle-product-tab]')];
    const pickerCurrent = drawer.querySelector('[data-bundle-picker-current]');
    const pickerPrevious = drawer.querySelector('[data-bundle-picker-previous]');
    const pickerNext = drawer.querySelector('[data-bundle-picker-next]');
    const panels = [...drawer.querySelectorAll('[data-bundle-product-panel]')];
    const forms = [...drawer.querySelectorAll('[data-bundle-form]')];
    const liveRegion = drawer.querySelector('[data-bundle-live]');
    const selectionCount = drawer.querySelector('[data-bundle-selection-count]');
    const discountMessage = drawer.querySelector('[data-bundle-discount-message]');
    const submitButton = drawer.querySelector('[data-bundle-submit]');
    const submitLabel = submitButton?.querySelector('[data-bundle-submit-label]');
    const submitStatus = drawer.querySelector('[data-bundle-submit-status]');
    const cartApi = getCartApiConfig();
    const parentProductId = Number.parseInt(drawer.dataset.parentProductId || '0', 10);
    const discountPercentage = Math.max(0, Number(drawer.dataset.bundleDiscountPercentage) || 0);
    const discountMinimum = Math.max(0, Number.parseInt(drawer.dataset.bundleDiscountMinimum || '0', 10));
    const selectedItems = new Map();
    const formControllers = new Map();
    let returnFocus = null;
    let closeTimer = 0;
    let focusTimer = 0;
    let pickerScrollTimer = 0;
    let suppressPickerSyncUntil = 0;

    if (drawer.parentElement !== document.body) {
        document.body.append(drawer);
    }

    const announce = (message) => {
        if (!liveRegion) {
            return;
        }

        liveRegion.textContent = '';
        window.setTimeout(() => {
            liveRegion.textContent = String(message || '');
        }, 20);
    };

    const setDrawerInert = (inert) => {
        drawer.inert = inert;

        if (inert) {
            drawer.setAttribute('inert', '');
        } else {
            drawer.removeAttribute('inert');
        }
    };

    const getFocusable = () => [...surface.querySelectorAll(drawerFocusableSelector)].filter(
        (element) => element.getClientRects().length > 0 && !element.closest('[hidden], [inert]'),
    );

    const isOpen = () => drawer.classList.contains('is-open');

    const finishClose = () => {
        drawer.dataset.state = 'closed';
        drawer.classList.remove('is-closing');
        setDrawerInert(true);
    };

    const close = ({ restoreFocus = true } = {}) => {
        if (!isOpen() || drawer.dataset.state === 'closing') {
            return;
        }

        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        window.clearTimeout(pickerScrollTimer);
        drawer.dataset.state = 'closing';
        drawer.classList.remove('is-open');
        drawer.classList.add('is-closing');
        drawer.setAttribute('aria-hidden', 'true');
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        document.documentElement.classList.remove('has-bundle-drawer-open');

        if (restoreFocus && returnFocus instanceof HTMLElement) {
            returnFocus.focus({ preventScroll: true });
        } else if (document.activeElement instanceof HTMLElement && drawer.contains(document.activeElement)) {
            document.activeElement.blur();
        }

        returnFocus = null;

        if (reducedMotionQuery.matches) {
            finishClose();
            return;
        }

        closeTimer = window.setTimeout(finishClose, 360);
    };

    const open = () => {
        if (isOpen()) {
            surface.querySelector('[data-bundle-close]')?.focus({ preventScroll: true });
            return;
        }

        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        returnFocus = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : primaryTrigger;
        setDrawerInert(false);
        drawer.classList.remove('is-closing');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.dataset.state = 'opening';
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
        document.documentElement.classList.add('has-bundle-drawer-open');

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                drawer.classList.add('is-open');
                drawer.dataset.state = 'open';
                const activeIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

                if (activeIndex >= 0) {
                    syncPickerNavigation(activeIndex);
                    scrollPickerToIndex(activeIndex, 'auto');
                }
            });
        });

        focusTimer = window.setTimeout(() => {
            surface.querySelector('[data-bundle-close]')?.focus({ preventScroll: true });
        }, reducedMotionQuery.matches ? 0 : 180);
    };

    const syncPickerNavigation = (index = 0) => {
        const safeIndex = Math.max(0, Math.min(tabs.length - 1, index));

        if (pickerCurrent) {
            pickerCurrent.textContent = String(safeIndex + 1).padStart(2, '0');
        }

        if (pickerPrevious) {
            pickerPrevious.disabled = safeIndex <= 0;
        }

        if (pickerNext) {
            pickerNext.disabled = safeIndex >= tabs.length - 1;
        }
    };

    const scrollPickerToIndex = (index, behavior = reducedMotionQuery.matches ? 'auto' : 'smooth') => {
        const tab = tabs[index];

        if (!picker || !tab || !mobileGalleryQuery.matches) {
            return;
        }

        const pickerRect = picker.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const target = picker.scrollLeft
            + (tabRect.left - pickerRect.left)
            - Math.max(0, (picker.clientWidth - tabRect.width) / 2);
        const maxScroll = Math.max(0, picker.scrollWidth - picker.clientWidth);

        suppressPickerSyncUntil = window.performance.now() + (behavior === 'smooth' ? 520 : 80);
        picker.scrollTo({
            left: Math.max(0, Math.min(maxScroll, target)),
            behavior,
        });
    };

    const setActiveProduct = (
        productId,
        { focusTab = false, scrollPicker = true, scrollPanel = true } = {},
    ) => {
        const id = String(productId || '');
        let activeIndex = -1;

        tabs.forEach((tab, tabIndex) => {
            const active = tab.dataset.bundleProductTab === id;
            tab.classList.toggle('is-selected', active);
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;

            if (active) {
                activeIndex = tabIndex;
            }

            if (active && focusTab) {
                tab.focus({ preventScroll: true });
            }
        });

        panels.forEach((panel) => {
            const active = panel.dataset.bundleProductPanel === id;
            panel.classList.toggle('is-active', active);
            panel.toggleAttribute('hidden', !active);
        });

        if (activeIndex >= 0) {
            syncPickerNavigation(activeIndex);

            if (scrollPicker) {
                scrollPickerToIndex(activeIndex);
            }
        }

        if (scrollPanel) {
            drawer.querySelector(`[data-bundle-product-panel="${CSS.escape(id)}"]`)?.scrollIntoView({
                behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
                block: 'nearest',
            });
        }
    };

    const productCountLabel = (count) => {
        if (count === 1) {
            return 'produkt';
        }

        if (count >= 2 && count <= 4) {
            return 'produkty';
        }

        return 'produktów';
    };

    const getFormProductId = (form) => Number.parseInt(form.dataset.productId || '0', 10);

    const getIncompleteOptionsLabel = (form) => {
        const labels = [...form.querySelectorAll('[data-bundle-options] legend > span:first-child')]
            .map((label) => label.textContent?.trim().toLocaleLowerCase('pl') || '');

        if (labels.includes('kolor') && labels.includes('rozmiar')) {
            return 'Wybierz kolor i rozmiar';
        }

        return labels.length === 1 && labels[0]
            ? `Wybierz ${labels[0]}`
            : 'Wybierz opcje';
    };

    const getFormPayload = (form) => {
        const productId = getFormProductId(form);
        const controller = formControllers.get(form) || null;
        const isVariable = form.dataset.productType === 'variable';
        const variation = controller?.getVariation() || null;

        if (productId <= 0 || (isVariable && !variation)) {
            return null;
        }

        return {
            product_id: productId,
            variation_id: Number.parseInt(variation?.variation_id || '0', 10),
            quantity: 1,
            variation: controller?.getSelections() || {},
            name: form.dataset.productName || 'Produkt',
            price: Number(variation?.display_price ?? form.dataset.bundleProductPrice) || 0,
            discount_eligible: form.dataset.bundleDiscountEligible === 'true',
        };
    };

    const syncFormState = (form) => {
        const productId = getFormProductId(form);
        const selected = selectedItems.has(productId);
        const payload = getFormPayload(form);
        const button = form.querySelector('[data-bundle-add]');
        const label = button?.querySelector('[data-bundle-add-label]');
        const panel = form.closest('[data-bundle-product-panel]');
        const tab = drawer.querySelector(`[data-bundle-product-tab="${CSS.escape(String(productId))}"]`);
        const pickerState = tab?.querySelector('[data-bundle-picker-state]');

        form.dataset.bundleSelected = String(selected);
        panel?.classList.toggle('is-in-bundle', selected);
        tab?.classList.toggle('is-in-bundle', selected);

        if (pickerState) {
            pickerState.hidden = !selected;
        }

        if (!button) {
            return;
        }

        const enabled = selected || Boolean(payload);
        button.disabled = !enabled;
        button.classList.toggle('disabled', !enabled);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-disabled', String(!enabled));

        if (label) {
            label.textContent = selected
                ? 'Usuń z zestawu'
                : (payload ? 'Dodaj do zestawu' : getIncompleteOptionsLabel(form));
        }
    };

    const syncSelectionSummary = () => {
        const count = selectedItems.size;
        const eligibleCount = [...selectedItems.values()].filter((item) => item.discount_eligible).length;

        drawer.classList.toggle('has-selection', count > 0);
        drawer.dataset.bundleSelectionCount = String(count);

        if (selectionCount) {
            selectionCount.textContent = String(count);
        }

        if (submitButton && !submitButton.classList.contains('is-loading')) {
            submitButton.disabled = count === 0;
            submitButton.setAttribute('aria-disabled', String(count === 0));
        }

        if (submitLabel && !submitButton?.classList.contains('is-loading')) {
            submitLabel.textContent = count > 0
                ? `Dodaj ${count} ${productCountLabel(count)} do koszyka`
                : 'Wybierz produkt';
        }

        if (discountMessage) {
            if (discountPercentage <= 0 || discountMinimum <= 0) {
                discountMessage.textContent = 'Możesz dodać jeden albo kilka produktów.';
            } else if (eligibleCount >= discountMinimum) {
                discountMessage.textContent = `Rabat −${String(discountPercentage).replace('.', ',')}% zostanie naliczony w koszyku.`;
            } else {
                const remaining = discountMinimum - eligibleCount;
                discountMessage.textContent = `Dodaj jeszcze ${remaining} ${productCountLabel(remaining)}, aby otrzymać −${String(discountPercentage).replace('.', ',')}%.`;
            }
        }

        forms.forEach(syncFormState);
    };

    const updateVariationImage = (image, variation, initialImage) => {
        if (!(image instanceof HTMLImageElement)) {
            return;
        }

        const variationImage = variation?.image || null;
        const source = variationImage?.full_src || variationImage?.src || initialImage.src;

        if (source) {
            image.src = source;
        }

        image.srcset = variationImage?.srcset || initialImage.srcset;
        image.sizes = variationImage?.sizes || initialImage.sizes;
        image.alt = variationImage?.alt || initialImage.alt;
    };

    const initializeVariableForm = (form) => {
        const groups = [...form.querySelectorAll('[data-bundle-options]')];
        const attributeNames = groups.map((group) => group.dataset.bundleOptions || '').filter(Boolean);
        const panel = form.closest('[data-bundle-product-panel]');
        const price = panel?.querySelector('[data-bundle-price]');
        const savings = panel?.querySelector('[data-bundle-savings]');
        const image = panel?.querySelector('[data-bundle-product-image]');
        const variationInput = form.querySelector('input.variation_id');
        const addButton = form.querySelector('[data-bundle-add]');
        const addLabel = addButton?.querySelector('[data-bundle-add-label]');
        const status = form.querySelector('[data-bundle-status]');
        const initialPrice = price?.innerHTML || '';
        const initialPricing = {
            savingsLabel: savings?.textContent?.trim() || '',
            savingsHidden: savings?.hidden ?? true,
        };
        const initialImage = {
            src: image?.getAttribute('src') || '',
            srcset: image?.getAttribute('srcset') || '',
            sizes: image?.getAttribute('sizes') || '',
            alt: image?.getAttribute('alt') || '',
        };
        let variations = [];
        let matchedVariation = null;
        let changeListener = null;

        const renderPricing = (pricing = null) => {
            const state = pricing
                ? {
                    savingsLabel: String(pricing.savings_label || ''),
                    savingsHidden: !pricing.promotion || !pricing.savings_label,
                }
                : initialPricing;

            if (savings) {
                savings.textContent = state.savingsLabel;
                savings.hidden = state.savingsHidden;
            }
        };

        try {
            variations = JSON.parse(form.querySelector('[data-bundle-variations]')?.textContent || '[]');
        } catch {
            variations = [];
        }

        const getSelections = () => Object.fromEntries(groups.map((group) => {
            const name = group.dataset.bundleOptions || '';
            const select = group.querySelector('[data-bundle-select]');

            return [name, select?.value || ''];
        }));

        const sync = () => {
            const selections = getSelections();

            groups.forEach((group) => {
                const attributeName = group.dataset.bundleOptions || '';
                const select = group.querySelector('[data-bundle-select]');
                const selectedLabel = group.querySelector('[data-bundle-selected-label]');

                group.querySelectorAll('[data-bundle-option-value]').forEach((option) => {
                    const value = option.dataset.bundleOptionValue || '';
                    const selected = select?.value === value;
                    const available = isBundleOptionAvailable(
                        variations,
                        selections,
                        attributeNames,
                        attributeName,
                        value,
                    );

                    option.classList.toggle('is-selected', selected);
                    option.classList.toggle('is-unavailable', !available);
                    option.setAttribute('aria-pressed', String(selected));
                    option.disabled = !available;
                });

                const selectedOption = group.querySelector('.is-selected[data-bundle-option-label]');

                if (selectedLabel) {
                    selectedLabel.textContent = selectedOption?.dataset.bundleOptionLabel || '';
                }
            });

            matchedVariation = findBundleVariation(variations, selections, attributeNames);

            if (variationInput) {
                variationInput.value = String(matchedVariation?.variation_id || 0);
            }

            if (price) {
                price.innerHTML = matchedVariation?.price_html || initialPrice;
            }

            renderPricing(matchedVariation?.seluvia_pricing || null);

            updateVariationImage(image, matchedVariation, initialImage);

            if (addButton) {
                const enabled = Boolean(matchedVariation);
                addButton.disabled = !enabled;
                addButton.classList.toggle('disabled', !enabled);
                addButton.setAttribute('aria-disabled', String(!enabled));
            }

            if (addLabel) {
                addLabel.textContent = matchedVariation
                    ? 'Dodaj do zestawu'
                    : getIncompleteOptionsLabel(form);
            }

            if (status && matchedVariation) {
                status.textContent = '';
            }

            if (typeof changeListener === 'function') {
                changeListener({ variation: matchedVariation, selections });
            }
        };

        groups.forEach((group) => {
            const select = group.querySelector('[data-bundle-select]');

            group.addEventListener('click', (event) => {
                const option = event.target instanceof Element
                    ? event.target.closest('[data-bundle-option-value]')
                    : null;

                if (!option || option.disabled || !select) {
                    return;
                }

                select.value = option.dataset.bundleOptionValue || '';
                sync();
            });
            select?.addEventListener('change', sync);
        });

        sync();

        return {
            getVariation: () => matchedVariation,
            getSelections,
            setChangeListener: (listener) => {
                changeListener = typeof listener === 'function' ? listener : null;
            },
        };
    };

    forms.forEach((form) => {
        const isVariable = form.dataset.productType === 'variable';
        const variationController = isVariable ? initializeVariableForm(form) : null;
        const status = form.querySelector('[data-bundle-status]');
        formControllers.set(form, variationController);

        variationController?.setChangeListener(() => {
            const productId = getFormProductId(form);

            if (selectedItems.delete(productId) && status) {
                status.textContent = 'Opcje zostały zmienione. Potwierdź produkt ponownie.';
            }

            syncSelectionSummary();
        });

        form.addEventListener('submit', (event) => {
            if (!window.fetch || !cartApi) {
                return;
            }

            event.preventDefault();
            const productId = getFormProductId(form);

            if (selectedItems.has(productId)) {
                selectedItems.delete(productId);

                if (status) {
                    status.textContent = 'Produkt został usunięty z wyboru.';
                }

                announce(`${form.dataset.productName || 'Produkt'} usunięto z zestawu.`);
                syncSelectionSummary();
                return;
            }

            const item = getFormPayload(form);

            if (!item) {
                if (status) {
                    status.textContent = 'Wybierz dostępny kolor i rozmiar.';
                }

                announce('Wybierz wszystkie opcje produktu.');
                return;
            }

            selectedItems.set(productId, item);

            if (status) {
                status.textContent = 'Wybrano. Dodaj kolejny produkt lub przejdź do koszyka.';
            }

            announce(`${item.name} dodano do wyboru.`);
            syncSelectionSummary();
        });
    });

    syncSelectionSummary();

    submitButton?.addEventListener('click', async () => {
        if (!cartApi || parentProductId <= 0 || selectedItems.size === 0) {
            return;
        }

        const items = [...selectedItems.values()].map((item) => ({
            product_id: item.product_id,
            variation_id: item.variation_id,
            quantity: 1,
            variation: item.variation,
        }));
        const count = items.length;
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        submitButton.setAttribute('aria-busy', 'true');

        if (submitLabel) {
            submitLabel.textContent = 'Dodawanie do koszyka…';
        }

        if (submitStatus) {
            submitStatus.textContent = '';
        }

        try {
            const payload = await requestBundleAddition(cartApi, {
                parent_product_id: parentProductId,
                items,
            });
            const message = `${count} ${productCountLabel(count)} dodano do koszyka.`;
            selectedItems.clear();
            submitButton.classList.remove('is-loading');
            submitButton.removeAttribute('aria-busy');
            syncSelectionSummary();

            if (submitStatus) {
                submitStatus.textContent = message;
            }

            announce(message);
            window.setTimeout(() => {
                close({ restoreFocus: false });
                window.setTimeout(() => {
                    document.dispatchEvent(new CustomEvent('seluvia:add-to-cart', {
                        detail: { cart: payload, button: submitButton },
                    }));
                }, reducedMotionQuery.matches ? 0 : 300);
            }, reducedMotionQuery.matches ? 0 : 220);
        } catch (requestError) {
            const message = requestError?.message || 'Nie udało się dodać wyboru. Spróbuj ponownie.';
            submitButton.classList.remove('is-loading');
            submitButton.removeAttribute('aria-busy');
            syncSelectionSummary();

            if (submitStatus) {
                submitStatus.textContent = message;
            }

            announce(message);
        }
    });

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            event.preventDefault();
            open();
        });
    });

    closeButtons.forEach((button) => button.addEventListener('click', () => close()));

    pickerPrevious?.addEventListener('click', () => {
        const activeIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const nextIndex = Math.max(0, activeIndex - 1);
        setActiveProduct(tabs[nextIndex]?.dataset.bundleProductTab, {
            focusTab: false,
            scrollPanel: false,
        });
    });

    pickerNext?.addEventListener('click', () => {
        const activeIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const nextIndex = Math.min(tabs.length - 1, Math.max(0, activeIndex) + 1);
        setActiveProduct(tabs[nextIndex]?.dataset.bundleProductTab, {
            focusTab: false,
            scrollPanel: false,
        });
    });

    picker?.addEventListener('scroll', () => {
        if (!mobileGalleryQuery.matches || window.performance.now() < suppressPickerSyncUntil) {
            return;
        }

        window.clearTimeout(pickerScrollTimer);
        pickerScrollTimer = window.setTimeout(() => {
            const pickerRect = picker.getBoundingClientRect();
            const pickerCenter = pickerRect.left + (pickerRect.width / 2);
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            tabs.forEach((tab, index) => {
                const rect = tab.getBoundingClientRect();
                const distance = Math.abs((rect.left + (rect.width / 2)) - pickerCenter);

                if (distance < closestDistance) {
                    closestIndex = index;
                    closestDistance = distance;
                }
            });

            if (tabs[closestIndex]?.getAttribute('aria-selected') !== 'true') {
                setActiveProduct(tabs[closestIndex]?.dataset.bundleProductTab, {
                    scrollPicker: false,
                    scrollPanel: false,
                });
            } else {
                syncPickerNavigation(closestIndex);
            }
        }, 120);
    }, { passive: true });

    tabs.forEach((tab, tabIndex) => {
        tab.addEventListener('click', () => setActiveProduct(tab.dataset.bundleProductTab));
        tab.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                return;
            }

            event.preventDefault();
            let nextIndex = tabIndex;

            if (event.key === 'Home') {
                nextIndex = 0;
            } else if (event.key === 'End') {
                nextIndex = tabs.length - 1;
            } else {
                nextIndex = (tabIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
            }

            setActiveProduct(tabs[nextIndex]?.dataset.bundleProductTab, { focusTab: true });
        });
    });

    syncPickerNavigation(0);

    drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }

        if (event.key !== 'Tab' || !isOpen()) {
            return;
        }

        const focusable = getFocusable();
        const first = focusable[0];
        const last = focusable.at(-1);

        if (!first || !last) {
            event.preventDefault();
            surface.focus({ preventScroll: true });
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    drawer.dataset.bundleReady = 'true';

    return { open, close };
};

const initAjaxAddToCart = (root) => {
    const purchase = root.querySelector('[data-product-purchase]');
    const form = purchase?.querySelector('form.cart');
    const status = purchase?.querySelector('[data-product-cart-status]');
    const cartApi = getCartApiConfig();
    const button = form?.querySelector('.single_add_to_cart_button');

    if (!form || !button || form.classList.contains('grouped_form') || !window.fetch || !cartApi) {
        return;
    }

    const originalLabel = button.textContent.trim();
    const label = document.createElement('span');
    label.className = 'SeluviaCartButton__label';
    label.textContent = originalLabel;
    const icon = document.createElement('span');
    icon.className = 'SeluviaCartButton__icon';
    icon.setAttribute('aria-hidden', 'true');
    button.replaceChildren(icon, label);
    button.classList.add('SeluviaCartButton');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let inFlight = false;
    let resetTimer = 0;

    const setLabel = (text) => {
        label.textContent = text;
        if (!reducedMotion.matches && typeof label.animate === 'function') {
            label.animate([
                { opacity: 0, transform: 'translateY(5px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        }
    };
    const resetButton = () => {
        button.classList.remove('is-loading', 'is-added');
        button.removeAttribute('aria-busy');
        setLabel(originalLabel);
    };

    form.addEventListener('submit', async (event) => {
        if (inFlight) {
            event.preventDefault();
            return;
        }
        if (button.disabled || button.classList.contains('disabled')) return;

        const isVariation = form.classList.contains('variations_form');
        const variationId = form.querySelector('input.variation_id')?.value || '0';
        const parentProductId = form.querySelector('input[name="product_id"]')?.value
            || form.dataset.product_id || button.value || '0';
        if ((isVariation && Number.parseInt(variationId, 10) <= 0)
            || Number.parseInt(parentProductId, 10) <= 0) return;

        event.preventDefault();
        window.clearTimeout(resetTimer);
        inFlight = true;
        const quantity = form.querySelector('input.qty')?.value || '1';
        const variation = {};
        new FormData(form).forEach((value, name) => {
            if (name.startsWith('attribute_') && typeof value === 'string') variation[name] = value;
        });
        const requestBody = {
            product_id: Number.parseInt(parentProductId, 10),
            variation_id: isVariation ? Number.parseInt(variationId, 10) : 0,
            quantity: Math.max(1, Number.parseInt(quantity, 10) || 1),
            variation,
        };

        button.disabled = true;
        button.classList.remove('is-added');
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        setLabel('Dodawanie…');
        if (status) {
            status.textContent = '';
            status.classList.remove('screen-reader-text');
        }

        try {
            const payload = await requestCartAddition(cartApi, requestBody);
            button.classList.remove('is-loading');
            button.classList.add('is-added');
            button.removeAttribute('aria-busy');
            setLabel('Dodano do koszyka');
            if (status) {
                status.classList.add('screen-reader-text');
                status.textContent = requestBody.quantity > 1
                    ? `Dodano ${requestBody.quantity} szt. do koszyka.`
                    : 'Produkt został dodany do koszyka.';
            }
            // Give the confirmation one short beat before the dialog enters.
            if (!reducedMotion.matches) await new Promise((resolve) => window.setTimeout(resolve, 340));
            button.disabled = false;
            document.dispatchEvent(new CustomEvent('seluvia:add-to-cart', {
                detail: { cart: payload, button },
            }));
            resetTimer = window.setTimeout(resetButton, 1400);
        } catch (requestError) {
            resetButton();
            if (status) {
                status.classList.remove('screen-reader-text');
                status.textContent = requestError?.message
                    || 'Nie udało się potwierdzić dodania. Sprawdź koszyk i spróbuj ponownie.';
            }
        } finally {
            inFlight = false;
            button.disabled = false;
        }
    });
};

const initReviewSystem = (root) => {
    const system = root.querySelector('[data-review-system]');

    if (!system || system.dataset.reviewReady === 'true') {
        return;
    }

    const endpoint = system.dataset.reviewEndpoint || '';
    const nonceEndpoint = system.dataset.reviewNonceEndpoint || '';
    const results = system.querySelector('[data-review-results]');
    const list = system.querySelector('[data-review-list]');
    const empty = system.querySelector('[data-review-empty]');
    const moreButton = system.querySelector('[data-review-more]');
    const requestStatus = system.querySelector('[data-review-request-status]');
    const sortSelect = system.querySelector('[data-review-sort]');
    const photosFilter = system.querySelector('[data-review-photos-filter]');
    const ratingButtons = [...system.querySelectorAll('[data-review-rating-filter]')];
    const dialog = system.querySelector('[data-review-dialog]');
    const form = dialog?.querySelector('[data-review-form]');
    const reviewNonceInput = form?.querySelector('input[name="seluvia_review_nonce"]');
    const formStatus = dialog?.querySelector('[data-review-form-status]');
    const success = dialog?.querySelector('[data-review-success]');
    const successMessage = dialog?.querySelector('[data-review-success-message]');
    const submitButton = dialog?.querySelector('[data-review-submit]');
    const fileInput = dialog?.querySelector('[data-review-files]');
    const dropzone = dialog?.querySelector('[data-review-dropzone]');
    const previews = dialog?.querySelector('[data-review-previews]');
    const commentField = dialog?.querySelector('textarea[name="comment"]');
    const characterCount = dialog?.querySelector('[data-review-character-count]');
    const ratingLabel = dialog?.querySelector('[data-review-rating-label]');
    const lightbox = system.querySelector('[data-review-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-review-lightbox-image]');
    const lightboxCounter = lightbox?.querySelector('[data-review-lightbox-counter]');
    const lightboxClose = lightbox?.querySelector('[data-review-lightbox-close]');
    const lightboxPrevious = lightbox?.querySelector('[data-review-lightbox-previous]');
    const lightboxNext = lightbox?.querySelector('[data-review-lightbox-next]');
    const maxPhotos = 4;
    const maxPhotoBytes = 5 * 1024 * 1024;
    const allowedPhotoTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const supportsFileTransfer = (() => {
        if (!('DataTransfer' in window)) {
            return false;
        }

        try {
            return new DataTransfer() instanceof DataTransfer;
        } catch (error) {
            return false;
        }
    })();
    const ratingMessages = {
        1: 'Nie spełnia oczekiwań',
        2: 'Poniżej oczekiwań',
        3: 'W porządku',
        4: 'Bardzo dobry wybór',
        5: 'Zachwycający model',
    };
    let page = Number.parseInt(system.dataset.reviewPage || '1', 10) || 1;
    let pages = Number.parseInt(system.dataset.reviewPages || '1', 10) || 1;
    let rating = 0;
    let withPhotos = false;
    let sort = sortSelect?.value || 'newest';
    let restNonce = system.dataset.reviewRestNonce || '';
    let nonceRequest = null;
    let controller = null;
    let returnFocus = null;
    let closeTimer = 0;
    let selectedFiles = [];
    let previewUrls = [];
    let lightboxItems = [];
    let lightboxIndex = 0;
    let lightboxReturnFocus = null;
    let lightboxCloseTimer = 0;
    let lightboxPointer = null;

    const setStatus = (element, message = '', state = '') => {
        if (!element) {
            return;
        }

        element.textContent = message;
        element.dataset.state = state;
    };

    const parseResponse = async (response) => {
        let payload = {};

        try {
            payload = await response.json();
        } catch (error) {
            payload = {};
        }

        if (!response.ok) {
            const requestError = new Error(payload?.message || 'Nie udało się wykonać operacji. Spróbuj ponownie.');
            requestError.status = response.status;
            requestError.code = payload?.code || '';
            requestError.payload = payload;
            throw requestError;
        }

        return payload;
    };

    const refreshReviewNonces = async () => {
        if (!nonceEndpoint || !window.fetch) {
            return false;
        }

        if (nonceRequest) {
            return nonceRequest;
        }

        nonceRequest = (async () => {
            const response = await fetch(nonceEndpoint, {
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
            });
            const payload = await parseResponse(response);
            const nonces = payload?.data || {};

            if (!payload?.success || !nonces.review_nonce || !nonces.rest_nonce) {
                throw new Error(nonces.message || 'Nie udało się odświeżyć sesji formularza.');
            }

            restNonce = nonces.rest_nonce;
            system.dataset.reviewRestNonce = restNonce;

            if (reviewNonceInput) {
                reviewNonceInput.value = nonces.review_nonce;
            }

            return true;
        })();

        try {
            return await nonceRequest;
        } finally {
            nonceRequest = null;
        }
    };

    const updateFilterState = () => {
        ratingButtons.forEach((button) => {
            const active = Number.parseInt(button.dataset.reviewRatingFilter || '0', 10) === rating;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-pressed', String(active));
        });
        photosFilter?.classList.toggle('is-active', withPhotos);
        photosFilter?.setAttribute('aria-pressed', String(withPhotos));
    };

    const requestReviews = async ({ append = false } = {}) => {
        if (!endpoint || !window.fetch || !list || !results) {
            return;
        }

        controller?.abort();
        controller = new AbortController();
        const requestController = controller;
        const requestedPage = append ? page + 1 : 1;
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('page', String(requestedPage));
        url.searchParams.set('rating', String(rating));
        url.searchParams.set('photos', withPhotos ? '1' : '0');
        url.searchParams.set('sort', sort);
        results.setAttribute('aria-busy', 'true');
        moreButton?.classList.add('is-loading');
        moreButton?.setAttribute('aria-busy', 'true');
        setStatus(requestStatus, append ? 'Wczytujemy kolejne opinie…' : 'Aktualizujemy opinie…');

        try {
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                signal: requestController.signal,
            });
            const payload = await parseResponse(response);

            if (append) {
                list.insertAdjacentHTML('beforeend', payload.html || '');
            } else {
                list.innerHTML = payload.html || '';
            }

            page = Number.parseInt(payload.page || String(requestedPage), 10) || requestedPage;
            pages = Number.parseInt(payload.pages || '1', 10) || 1;
            system.dataset.reviewPage = String(page);
            system.dataset.reviewPages = String(pages);
            const hasReviews = Number.parseInt(payload.total || '0', 10) > 0;

            if (empty) {
                empty.hidden = hasReviews;
                const heading = empty.querySelector('h3');

                if (heading && payload.empty_message) {
                    heading.textContent = payload.empty_message;
                }
            }

            if (moreButton) {
                moreButton.hidden = !payload.has_more;
            }

            setStatus(requestStatus, hasReviews
                ? `${payload.total} ${Number(payload.total) === 1 ? 'opinia' : 'opinii'}`
                : (payload.empty_message || ''), 'success');
        } catch (requestError) {
            if (requestError?.name === 'AbortError') {
                return;
            }

            setStatus(requestStatus, requestError?.message || 'Nie udało się wczytać opinii.', 'error');
        } finally {
            if (controller === requestController) {
                results.removeAttribute('aria-busy');
                moreButton?.classList.remove('is-loading');
                moreButton?.removeAttribute('aria-busy');
            }
        }
    };

    const updateSummary = (summary) => {
        if (!summary) {
            return;
        }

        const average = system.querySelector('[data-review-average]');
        const total = system.querySelector('[data-review-total]');
        const photoCount = system.querySelector('[data-review-photo-count]');
        const summaryStars = system.querySelector('[data-review-summary-stars]');

        if (average) {
            average.textContent = summary.average_label || '0,0';
        }

        if (total) {
            total.textContent = summary.total_label || '0 opinii';
        }

        if (photoCount) {
            photoCount.textContent = String(summary.photo_count || 0);
        }

        if (summaryStars) {
            const roundedAverage = Math.round(Number.parseFloat(summary.average || '0'));
            summaryStars.querySelectorAll(':scope > span').forEach((star, index) => {
                star.classList.toggle('is-filled', index < roundedAverage);
            });
            summaryStars.setAttribute('aria-label', `Średnia ocena: ${summary.average_label || '0,0'} na 5`);
        }

        (summary.distribution || []).forEach((row) => {
            system.querySelectorAll(`[data-review-rating-filter="${row.rating}"]`).forEach((button) => {
                if (!button.classList.contains('SeluviaReviews__distributionRow')) {
                    return;
                }

                const count = button.lastElementChild;
                const progress = button.querySelector('.SeluviaReviews__distributionTrack span');

                if (count) {
                    count.textContent = String(row.count || 0);
                }

                progress?.style.setProperty('--review-progress', `${row.percentage || 0}%`);
                button.setAttribute('aria-label', `${row.rating} gwiazdek: ${row.count || 0} opinii`);
            });
        });
    };

    const clearPreviewUrls = () => {
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        previewUrls = [];
    };

    const syncFileInput = () => {
        if (!fileInput || !supportsFileTransfer) {
            return false;
        }

        try {
            const transfer = new DataTransfer();
            selectedFiles.forEach((file) => transfer.items.add(file));
            fileInput.files = transfer.files;
            return true;
        } catch (error) {
            return false;
        }
    };

    const renderPreviews = () => {
        if (!previews) {
            return;
        }

        clearPreviewUrls();
        previews.replaceChildren();
        selectedFiles.forEach((file, index) => {
            const item = document.createElement('div');
            const image = document.createElement('img');
            const remove = document.createElement('button');
            const url = URL.createObjectURL(file);
            previewUrls.push(url);
            item.className = 'SeluviaReviewForm__preview';
            image.src = url;
            image.alt = `Podgląd zdjęcia ${index + 1}`;
            remove.type = 'button';
            remove.dataset.reviewRemoveFile = String(index);
            remove.setAttribute('aria-label', `Usuń zdjęcie ${index + 1}`);
            remove.innerHTML = '<span aria-hidden="true">×</span>';
            item.append(image, remove);
            previews.append(item);
        });
        syncFileInput();
    };

    const addFiles = (incoming) => {
        const next = [...selectedFiles];
        let error = '';

        [...incoming].forEach((file) => {
            if (next.length >= maxPhotos) {
                error = `Możesz dodać maksymalnie ${maxPhotos} zdjęcia.`;
                return;
            }

            if (!allowedPhotoTypes.includes(file.type)) {
                error = 'Dozwolone są zdjęcia JPG, PNG i WebP.';
                return;
            }

            if (file.size <= 0 || file.size > maxPhotoBytes) {
                error = 'Każde zdjęcie może mieć maksymalnie 5 MB.';
                return;
            }

            const duplicate = next.some((current) => (
                current.name === file.name
                && current.size === file.size
                && current.lastModified === file.lastModified
            ));

            if (!duplicate) {
                next.push(file);
            }
        });
        selectedFiles = next;
        renderPreviews();
        setStatus(formStatus, error, error ? 'error' : '');
    };

    const resetFormView = () => {
        if (!form) {
            return;
        }

        form.hidden = false;
        success?.setAttribute('hidden', '');
        submitButton?.classList.remove('is-loading');
        submitButton?.removeAttribute('aria-busy');
        setStatus(formStatus);
    };

    const finishDialogClose = () => {
        if (typeof dialog?.close === 'function' && dialog.open) {
            dialog.close();
        } else {
            dialog?.removeAttribute('open');
        }

        dialog?.classList.remove('is-visible', 'is-closing');
        document.documentElement.classList.remove('has-review-form-open');
        resetFormView();

        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
            returnFocus.focus({ preventScroll: true });
        }

        returnFocus = null;
    };

    const closeDialog = () => {
        if (!dialog?.open || dialog.classList.contains('is-closing')) {
            return;
        }

        window.clearTimeout(closeTimer);
        dialog.classList.remove('is-visible');
        dialog.classList.add('is-closing');

        if (reducedMotionQuery.matches) {
            finishDialogClose();
        } else {
            closeTimer = window.setTimeout(finishDialogClose, 300);
        }
    };

    const openDialog = (trigger) => {
        if (!dialog) {
            return;
        }

        window.clearTimeout(closeTimer);
        returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        resetFormView();
        dialog.classList.remove('is-visible', 'is-closing');

        if (!dialog.open && typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else if (!dialog.open) {
            dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('has-review-form-open');
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.add('is-visible')));
        window.setTimeout(() => dialog.querySelector('input[name="rating"]')?.focus({ preventScroll: true }), reducedMotionQuery.matches ? 0 : 180);
        refreshReviewNonces().catch(() => {});
    };

    const getLightboxItems = () => [...list?.querySelectorAll('[data-review-photo]') || []].map((button) => ({
        url: button.dataset.reviewPhotoFull || button.querySelector('img')?.src || '',
        alt: button.dataset.reviewPhotoAlt || button.querySelector('img')?.alt || '',
        button,
    })).filter((item) => item.url);

    const renderLightbox = (nextIndex) => {
        if (!lightboxImage || lightboxItems.length === 0) {
            return;
        }

        lightboxIndex = (nextIndex + lightboxItems.length) % lightboxItems.length;
        const item = lightboxItems[lightboxIndex];
        lightboxImage.classList.add('is-loading');
        lightboxImage.src = item.url;
        lightboxImage.alt = item.alt;
        lightboxImage.addEventListener('load', () => lightboxImage.classList.remove('is-loading'), { once: true });

        if (lightboxCounter) {
            lightboxCounter.textContent = `${String(lightboxIndex + 1).padStart(2, '0')} / ${String(lightboxItems.length).padStart(2, '0')}`;
        }

        const multiple = lightboxItems.length > 1;
        lightboxPrevious?.toggleAttribute('hidden', !multiple);
        lightboxNext?.toggleAttribute('hidden', !multiple);
    };

    const finishLightboxClose = () => {
        if (typeof lightbox?.close === 'function' && lightbox.open) {
            lightbox.close();
        } else {
            lightbox?.removeAttribute('open');
        }

        lightbox?.classList.remove('is-visible', 'is-closing');
        document.documentElement.classList.remove('has-review-lightbox-open');

        if (lightboxReturnFocus instanceof HTMLElement && lightboxReturnFocus.isConnected) {
            lightboxReturnFocus.focus({ preventScroll: true });
        }

        lightboxReturnFocus = null;
    };

    const closeLightbox = () => {
        if (!lightbox?.open || lightbox.classList.contains('is-closing')) {
            return;
        }

        window.clearTimeout(lightboxCloseTimer);
        lightbox.classList.remove('is-visible');
        lightbox.classList.add('is-closing');

        if (reducedMotionQuery.matches) {
            finishLightboxClose();
        } else {
            lightboxCloseTimer = window.setTimeout(finishLightboxClose, 260);
        }
    };

    const openLightbox = (trigger) => {
        if (!lightbox || !lightboxImage) {
            return;
        }

        lightboxItems = getLightboxItems();
        const nextIndex = Math.max(0, lightboxItems.findIndex((item) => item.button === trigger));
        lightboxReturnFocus = trigger;
        renderLightbox(nextIndex);
        lightbox.classList.remove('is-visible', 'is-closing');

        if (!lightbox.open && typeof lightbox.showModal === 'function') {
            lightbox.showModal();
        } else if (!lightbox.open) {
            lightbox.setAttribute('open', '');
        }

        document.documentElement.classList.add('has-review-lightbox-open');
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => lightbox.classList.add('is-visible')));
        lightboxClose?.focus({ preventScroll: true });
    };

    ratingButtons.forEach((button) => button.addEventListener('click', () => {
        const nextRating = Number.parseInt(button.dataset.reviewRatingFilter || '0', 10) || 0;

        if (nextRating === rating && nextRating !== 0) {
            rating = 0;
        } else {
            rating = nextRating;
        }

        updateFilterState();
        requestReviews();
    }));
    photosFilter?.addEventListener('click', () => {
        withPhotos = !withPhotos;
        updateFilterState();
        requestReviews();
    });
    sortSelect?.addEventListener('change', () => {
        sort = sortSelect.value;
        requestReviews();
    });
    moreButton?.addEventListener('click', () => requestReviews({ append: true }));

    system.addEventListener('click', (event) => {
        const openButton = event.target instanceof Element ? event.target.closest('[data-review-open]') : null;
        const photoButton = event.target instanceof Element ? event.target.closest('[data-review-photo]') : null;

        if (openButton) {
            openDialog(openButton);
        } else if (photoButton) {
            openLightbox(photoButton);
        }
    });

    dialog?.querySelectorAll('[data-review-close]').forEach((button) => button.addEventListener('click', closeDialog));
    dialog?.addEventListener('click', (event) => {
        if (event.target === dialog) {
            closeDialog();
        }
    });
    dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog();
    });
    dialog?.addEventListener('close', () => {
        dialog.classList.remove('is-visible', 'is-closing');
        document.documentElement.classList.remove('has-review-form-open');
    });

    dialog?.querySelectorAll('input[name="rating"]').forEach((input) => input.addEventListener('change', () => {
        if (ratingLabel) {
            ratingLabel.textContent = ratingMessages[input.value] || 'Wybrano ocenę';
        }
    }));
    commentField?.addEventListener('input', () => {
        if (characterCount) {
            characterCount.textContent = String(commentField.value.length);
        }
    });
    fileInput?.addEventListener('change', () => {
        addFiles(fileInput.files || []);
    });
    previews?.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-review-remove-file]') : null;

        if (!button) {
            return;
        }

        const index = Number.parseInt(button.dataset.reviewRemoveFile || '-1', 10);

        if (index >= 0) {
            selectedFiles.splice(index, 1);
            renderPreviews();
        }
    });
    ['dragenter', 'dragover'].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragging');
    }));
    dropzone?.addEventListener('drop', (event) => addFiles(event.dataTransfer?.files || []));

    const sendReview = async () => {
        syncFileInput();
        const formData = new FormData(form);
        const headers = { Accept: 'application/json' };
        formData.delete('review_photos[]');
        selectedFiles.forEach((file) => formData.append('review_photos[]', file, file.name));

        if (restNonce) {
            headers['X-WP-Nonce'] = restNonce;
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers,
            body: formData,
        });

        return parseResponse(response);
    };

    form?.addEventListener('submit', async (event) => {
        if (!window.fetch || !endpoint) {
            return;
        }

        event.preventDefault();
        setStatus(formStatus);

        if (!form.checkValidity()) {
            form.reportValidity();
            setStatus(formStatus, 'Uzupełnij wymagane pola formularza.', 'error');
            return;
        }

        if (selectedFiles.length > maxPhotos) {
            setStatus(formStatus, `Możesz dodać maksymalnie ${maxPhotos} zdjęcia.`, 'error');
            return;
        }

        submitButton?.classList.add('is-loading');
        submitButton?.setAttribute('aria-busy', 'true');
        submitButton?.setAttribute('disabled', '');
        setStatus(formStatus, 'Bezpiecznie przesyłamy Twoją opinię…');

        try {
            try {
                await refreshReviewNonces();
            } catch (nonceError) {
                // The embedded nonces remain a progressive fallback when the
                // lightweight refresh request is temporarily unavailable.
            }

            let payload;

            try {
                payload = await sendReview();
            } catch (requestError) {
                const expired = ['rest_cookie_invalid_nonce', 'seluvia_review_expired'].includes(requestError?.code);

                if (!expired) {
                    throw requestError;
                }

                await refreshReviewNonces();
                payload = await sendReview();
            }

            updateSummary(payload.summary);
            form.reset();
            selectedFiles = [];
            renderPreviews();

            if (characterCount) {
                characterCount.textContent = '0';
            }

            if (ratingLabel) {
                ratingLabel.textContent = 'Wybierz ocenę';
            }

            form.hidden = true;
            success?.removeAttribute('hidden');

            if (successMessage) {
                successMessage.textContent = payload.message || 'Opinia została zapisana.';
            }

            success?.querySelector('button')?.focus({ preventScroll: true });
            rating = 0;
            withPhotos = false;
            sort = 'newest';

            if (sortSelect) {
                sortSelect.value = sort;
            }

            updateFilterState();
            requestReviews();
        } catch (requestError) {
            setStatus(formStatus, requestError?.message || 'Nie udało się wysłać opinii.', 'error');
            formStatus?.focus?.({ preventScroll: true });
        } finally {
            submitButton?.classList.remove('is-loading');
            submitButton?.removeAttribute('aria-busy');
            submitButton?.removeAttribute('disabled');
        }
    });

    lightboxClose?.addEventListener('click', closeLightbox);
    lightboxPrevious?.addEventListener('click', () => renderLightbox(lightboxIndex - 1));
    lightboxNext?.addEventListener('click', () => renderLightbox(lightboxIndex + 1));
    lightbox?.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target === lightbox.querySelector('[data-review-lightbox-stage]')) {
            closeLightbox();
        }
    });
    lightbox?.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeLightbox();
    });
    lightbox?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            renderLightbox(lightboxIndex - 1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            renderLightbox(lightboxIndex + 1);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            closeLightbox();
        }
    });
    lightbox?.addEventListener('pointerdown', (event) => {
        lightboxPointer = { x: event.clientX, y: event.clientY };
    });
    lightbox?.addEventListener('pointerup', (event) => {
        if (!lightboxPointer) {
            return;
        }

        const deltaX = event.clientX - lightboxPointer.x;
        const deltaY = event.clientY - lightboxPointer.y;
        lightboxPointer = null;

        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
            renderLightbox(lightboxIndex + (deltaX < 0 ? 1 : -1));
        }
    });

    updateFilterState();
    system.dataset.reviewReady = 'true';
};

const initProduct = (root) => {
    if (root.dataset.productReady === 'true') {
        return;
    }

    const gallery = root.querySelector('[data-product-gallery]');

    if (gallery) {
        initGalleryDialog(gallery);
        initMobileGallery(gallery);
        initVisibleVideos(gallery);
    }

    initVariationOptions(root);
    initColorFamily(root);
    initBundleDrawer(root);
    initDisclosures(root);
    initAjaxAddToCart(root);
    initReviewSystem(root);
    root.dataset.productReady = 'true';
};

const init = () => {
    document.querySelectorAll('[data-product-root]').forEach(initProduct);
    initSizeGuides();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
